import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Rocket, CheckCircle2, AlertCircle, Globe } from "lucide-react";

/**
 * PublishChangesButton — kicks off an Astro rebuild + poll the server
 * for progress. Once complete the marketing site reflects all pending
 * CMS edits without any manual shell work.
 *
 * Backend endpoints:
 *   POST /api/admin/sbt-site/publish          → starts a background build
 *   GET  /api/admin/sbt-site/publish-status   → { status, last_success_at, ... }
 */
export default function PublishChangesButton() {
  const [state, setState] = useState({ status: "idle", last_success_at: null, message: "" });
  const [liveHook, setLiveHook] = useState({ configured: false, triggering: false });
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/admin/sbt-site/live-hook-status")
      .then((r) => setLiveHook((s) => ({ ...s, configured: !!r.data?.configured })))
      .catch(() => {});
  }, []);

  const publishLive = async () => {
    setLiveHook((s) => ({ ...s, triggering: true }));
    try {
      await api.post("/admin/sbt-site/publish-live");
      toast.success("Live website rebuild triggered — the host will deploy the fresh build in ~1–2 minutes.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not trigger the live deploy hook.");
    } finally {
      setLiveHook((s) => ({ ...s, triggering: false }));
    }
  };

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/sbt-site/publish-status");
      setState(r.data);
      return r.data;
    } catch (e) {
      /* silent — button is a helper, not critical to admin flow */
      return null;
    }
  }, []);

  useEffect(() => { fetchStatus(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, [fetchStatus]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await fetchStatus();
      if (!s) return;
      if (s.status === "success") {
        clearInterval(pollRef.current); pollRef.current = null;
        toast.success(`Published live in ${Math.round((s.duration_ms || 0) / 1000)}s. The marketing site now reflects your changes.`);
      } else if (s.status === "failed") {
        clearInterval(pollRef.current); pollRef.current = null;
        toast.error(`Publish failed: ${s.message || "unknown error"}`);
      }
    }, 1500);
  }, [fetchStatus]);

  const publish = async () => {
    try {
      const r = await api.post("/admin/sbt-site/publish");
      setState(r.data);
      if (r.data.already_building) {
        toast.info("A publish is already in progress.");
      } else {
        toast.info("Publishing… this usually takes ~5 seconds.");
      }
      startPolling();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Publish failed to start");
    }
  };

  const isBuilding = state.status === "building";
  const lastText = state.last_success_at
    ? `Last published ${timeAgo(state.last_success_at)}`
    : "Never published yet — click Publish to push CMS edits live.";

  return (
    <div className="flex items-center gap-3" data-testid="publish-changes-container">
      <div className="text-right hidden md:block">
        <div className="text-[11px] uppercase tracking-wider text-slate-400">Publish Status</div>
        <div className="text-xs text-slate-600 flex items-center gap-1.5 justify-end">
          {state.status === "success" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/>}
          {state.status === "failed" && <AlertCircle className="w-3.5 h-3.5 text-red-600"/>}
          {isBuilding && <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-600"/>}
          <span>{isBuilding ? "Building…" : lastText}</span>
        </div>
      </div>
      <Button
        size="sm"
        onClick={publish}
        disabled={isBuilding}
        className="bg-cyan-600 hover:bg-cyan-700 text-white shadow-lg"
        data-testid="publish-changes-btn"
      >
        {isBuilding ? (
          <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> Publishing…</>
        ) : (
          <><Rocket className="w-3.5 h-3.5 mr-1.5"/> Publish Changes</>
        )}
      </Button>
      {liveHook.configured && (
        <Button
          size="sm"
          onClick={publishLive}
          disabled={liveHook.triggering}
          className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg"
          data-testid="publish-live-website-btn"
          title="Triggers the deploy hook of the standalone website host (rebuild + redeploy of www)"
        >
          {liveHook.triggering ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> Triggering…</>
          ) : (
            <><Globe className="w-3.5 h-3.5 mr-1.5"/> Publish to Live Website</>
          )}
        </Button>
      )}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60)  return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
